import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import Modal from "@/components/admin/Modal";
import { Field, TextInput, TextArea, Tag } from "@/components/admin/FormControls";
import ImageUploader from "@/components/admin/ImageUploader";
import { Plus, Pencil, Trash2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (url) => !url ? "" : (url.startsWith("http") ? url : `${BACKEND}${url}`);

const empty = { name: "", title: "", bio: "", specialties: [], photo: "", instagram: "" };

export default function AdminMentors() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [specInput, setSpecInput] = useState("");
    const [error, setError] = useState("");

    const load = () => api.get("/admin/mentors").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openCreate = () => { setForm(empty); setEditing(null); setError(""); setOpen(true); };
    const openEdit = (m) => { setForm({ ...empty, ...m }); setEditing(m.id); setError(""); setOpen(true); };

    const save = async () => {
        setError("");
        try {
            const body = { ...form, specialties: form.specialties || [] };
            if (editing) await api.put(`/admin/mentors/${editing}`, body);
            else await api.post("/admin/mentors", body);
            setOpen(false);
            await load();
        } catch (e) { setError(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Excluir este mentor? Esta ação não pode ser desfeita.")) return;
        try { await api.delete(`/admin/mentors/${id}`); await load(); }
        catch (e) { alert(formatApiError(e)); }
    };

    const addSpec = () => {
        const v = specInput.trim();
        if (!v) return;
        setForm({ ...form, specialties: [...(form.specialties || []), v] });
        setSpecInput("");
    };

    return (
        <AdminLayout
            eyebrow="Equipe"
            title="Mentores"
            actions={<button onClick={openCreate} data-testid="mentor-create-btn" className="btn-primary"><Plus className="w-4 h-4" /> Novo mentor</button>}
        >
            <div data-testid="mentors-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {items.map(m => (
                    <div key={m.id} data-testid={`mentor-row-${m.id}`} className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                        {m.photo ? (
                            <img src={resolveSrc(m.photo)} alt={m.name} className="w-full aspect-[3/2] object-cover object-top" />
                        ) : (
                            <div className="w-full aspect-[3/2] bg-[#F8F8F5] flex items-center justify-center font-display text-3xl text-[#5C5C5C]">
                                {m.name?.[0] || "?"}
                            </div>
                        )}
                        <div className="p-5">
                            <div className="font-display text-lg line-clamp-1">{m.name}</div>
                            <div className="font-body text-xs text-[#5C5C5C] mt-1 line-clamp-1">{m.title}</div>
                            <div className="flex flex-wrap gap-1.5 mt-3 min-h-[34px]">
                                {(m.specialties || []).slice(0, 3).map(s => <Tag key={s}>{s}</Tag>)}
                            </div>
                            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-black/5">
                                <button onClick={() => openEdit(m)} data-testid={`mentor-edit-${m.id}`} className="btn-ghost !py-2 !px-3"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                                <button onClick={() => remove(m.id)} data-testid={`mentor-delete-${m.id}`} className="btn-ghost !py-2 !px-3 !text-red-600 !border-red-200"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="col-span-full text-center font-body text-[#5C5C5C] py-12">Nenhum mentor cadastrado ainda.</div>}
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar mentor" : "Novo mentor"}>
                <div className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                        <Field label="Nome"><TextInput testId="mentor-name" value={form.name} onChange={v => setForm({ ...form, name: v })} placeholder="Dr. Nome Sobrenome" /></Field>
                        <Field label="Título"><TextInput testId="mentor-title" value={form.title} onChange={v => setForm({ ...form, title: v })} placeholder="Médico Integrativo" /></Field>
                    </div>
                    <Field label="Bio"><TextArea testId="mentor-bio" value={form.bio} onChange={v => setForm({ ...form, bio: v })} rows={4} placeholder="História, formação..." /></Field>
                    <Field label="Foto" hint="Arraste ou clique para enviar — comprimida automaticamente.">
                        <ImageUploader
                            testId="mentor-photo-uploader"
                            value={form.photo}
                            onChange={(url) => setForm({ ...form, photo: url })}
                            aspect="aspect-[4/3]"
                        />
                    </Field>
                    <Field label="Instagram"><TextInput testId="mentor-instagram" value={form.instagram} onChange={v => setForm({ ...form, instagram: v })} placeholder="@usuario" /></Field>
                    <Field label="Especialidades">
                        <div className="flex gap-2">
                            <TextInput testId="mentor-spec-input" value={specInput} onChange={setSpecInput} placeholder="Ex.: Medicina Quântica" />
                            <button type="button" onClick={addSpec} className="btn-ghost shrink-0">Adicionar</button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {(form.specialties || []).map((s, i) => (
                                <Tag key={i} onRemove={() => setForm({ ...form, specialties: form.specialties.filter((_, j) => j !== i) })}>{s}</Tag>
                            ))}
                        </div>
                    </Field>
                    {error && <div data-testid="mentor-error" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>}
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={() => setOpen(false)} className="btn-ghost">Cancelar</button>
                        <button onClick={save} data-testid="mentor-save" className="btn-primary">Salvar</button>
                    </div>
                </div>
            </Modal>
        </AdminLayout>
    );
}
